import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import loteService, { ProductoLote } from '../../services/loteService';
import { getApiErrorMessage } from '../../utils/apiError';

const LotesPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<ProductoLote[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [codigoArticulo, setCodigoArticulo] = useState('');
  const [nroLote, setNroLote] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await loteService.search({
        codigoArticulo: codigoArticulo || undefined,
        nroLote: nroLote || undefined,
        page: page + 1,
        pageSize,
        soloConSaldo: false,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al cargar lotes'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Lotes y series
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Artículo"
              size="small"
              value={codigoArticulo}
              onChange={(e) => setCodigoArticulo(e.target.value)}
              fullWidth
            />
            <TextField
              label="Nro lote/serie"
              size="small"
              value={nroLote}
              onChange={(e) => setNroLote(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={() => {
                setPage(0);
                load();
              }}
              disabled={loading}
            >
              Buscar
            </Button>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Artículo</TableCell>
                  <TableCell>Lote/Serie</TableCell>
                  <TableCell>Vencimiento</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Saldo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((l) => (
                  <TableRow
                    key={l.idLote}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/inventario/lotes/${l.idLote}`)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {l.codigoArticulo}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {l.descripcionArticulo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {l.nroLote}{' '}
                      {l.esSerie && <Chip size="small" label="Serie" sx={{ ml: 0.5 }} />}
                    </TableCell>
                    <TableCell>
                      {l.fechaVencimiento ? String(l.fechaVencimiento).slice(0, 10) : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={l.estado === 'B' ? 'Bloqueado' : 'Activo'}
                        color={l.estado === 'B' ? 'default' : 'success'}
                      />
                    </TableCell>
                    <TableCell align="right">{l.cantidadTotal}</TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        Sin resultados
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Filas"
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default LotesPage;
